// canvas/trump-badge.js: Pillar 2 persistent trump indicator (M2.c-5).
//
// Extracted from table.js to give the trump renderer its own module and
// expose a small companion (`renderTrumpPip`) the scoreboard mirrors.
//
// Two exports:
//   • renderTrumpBadge(ctx, layout, state), the ≥48px badge anchored
//     adjacent to maker_seat's nameplate. Shows symbol + suit name +
//     suit-natural background. Pre-call falls back to em-dash + "No
//     trump" muted (matches M2.c-1 visual decision; plan v2 said "no
//     badge" but the muted placeholder anchors the eye to the right
//     spot before bidding completes).
//   • renderTrumpPip(ctx, suit, cx, cy, size), a tiny color-aware pip
//     used inside the scoreboard panel. ≤24px per plan AC #5. Renders
//     nothing when suit is null.
//
// Both channels (color + glyph) are always present per OQ-7, color
// alone is never the cue.

import { _internals as cardInternals } from './cards.js';
import { canvasFont, fitFontSize, truncateLabelToFit } from './text.js';

const { SUIT_SYMBOL, SUIT_NAME } = cardInternals;

const COLOR_SUIT_RED    = '#E03048';  // hearts, diamonds
const COLOR_SUIT_DARK   = '#1A2540';  // clubs, spades
const COLOR_BADGE_NONE  = '#3A3A3A';  // muted when trump not yet called
const COLOR_BADGE_TEXT  = '#FFFFFF';
const COLOR_LABEL_MUTED = '#8A8A8A';

export function suitColor(suit) {
  if (!suit) return COLOR_BADGE_NONE;
  return (suit === 'H' || suit === 'D') ? COLOR_SUIT_RED : COLOR_SUIT_DARK;
}

function fillRoundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
  ctx.closePath();
  ctx.fill();
}

const LABEL_GAP = 4;
const LABEL_H = 14;
const LABEL_EXTRA_W = 28;  // the suit-name label box is badgeW + 28 wide

/**
 * Y3: the badge's box, shared by the renderer, the trump splash (which
 * must stay off it) and the view's data-trump-badge seam. Portrait and
 * landscape phones pin it in the layout; desktop parks it beside the
 * maker's plate (beside the You plate before trump is called). Pure.
 *
 * @param {import('./layout.js').TableLayout} layout
 * @param {{maker_seat?: number|null}} state
 * @returns {{x:number, y:number, w:number, h:number}|null}
 */
export function trumpBadgeRect(layout, state) {
  const { badgeW, badgeH, canvasW, canvasH } = layout;
  if (layout.trumpBadge) return { ...layout.trumpBadge };
  const stackH = badgeH + LABEL_GAP + LABEL_H;
  // Anchor adjacent to maker_seat once bidding completes; before that,
  // park next to seat 1 (you) so the player always knows where to look.
  const anchorSeatId = state?.maker_seat || 1;
  const anchor = layout.seats.find((s) => s.id === anchorSeatId);
  if (!anchor) return null;
  let bx;
  let by;
  // Position: prefer the inner-canvas side of the plate. S/N seats sit
  // to the side; W/E seats stack below.
  if (anchor.align === 'bottom' || anchor.align === 'top') {
    bx = anchor.x + anchor.w + 8;
    if (bx + badgeW > canvasW - 4) bx = anchor.x - badgeW - 8;
    by = anchor.y + Math.round((anchor.h - badgeH) / 2);
  } else {
    bx = anchor.x + Math.round((anchor.w - badgeW) / 2);
    by = anchor.y + anchor.h + 8;
  }
  // Final clamp so the full badge + label stack stays on canvas (without
  // this the suit-name label clips off the bottom edge on tight mobile).
  if (by + stackH > canvasH - 4) by = Math.max(4, canvasH - 4 - stackH);
  if (bx + badgeW > canvasW - 4) bx = Math.max(4, canvasW - 4 - badgeW);
  return { x: bx, y: by, w: badgeW, h: badgeH };
}

/**
 * The badge plus its suit-name label (18px row below, badgeW + 28 wide,
 * centred): the whole area the renderer may paint.
 *
 * @param {import('./layout.js').TableLayout} layout
 * @param {{maker_seat?: number|null}} state
 * @returns {{x:number, y:number, w:number, h:number}|null}
 */
export function trumpBadgeStackRect(layout, state) {
  const r = trumpBadgeRect(layout, state);
  if (!r) return null;
  return {
    x: r.x - LABEL_EXTRA_W / 2,
    y: r.y,
    w: r.w + LABEL_EXTRA_W,
    h: r.h + LABEL_GAP + LABEL_H,
  };
}

/**
 * B4b-1 A6: the text under the badge. Phones pin the badge in one spot
 * (layout.trumpBadge), so it reads as if the nearest plate called trump;
 * there the label names the caller ("♥ by Bot Caprica"). Desktop parks
 * the badge by the maker's plate and keeps the suit name. Pure.
 *
 * @param {import('./layout.js').TableLayout} layout
 * @param {{trump_suit?: string|null, maker_seat?: number|null}} state
 * @param {Record<number, string>|undefined} names  lua seat → plain name
 * @returns {string}
 */
export function trumpBadgeLabel(layout, state, names) {
  if (!state?.trump_suit) return 'No trump';
  const caller = state.maker_seat ? names?.[state.maker_seat] : null;
  if (layout.trumpBadge && caller) return `${SUIT_SYMBOL[state.trump_suit]} by ${caller}`;
  return SUIT_NAME[state.trump_suit];
}

/**
 * Render the primary trump badge adjacent to maker_seat's nameplate.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {import('./layout.js').TableLayout} layout
 * @param {{trump_suit?: string|null, maker_seat?: number|null}} state
 * @param {{makerName?: string|null}} [opts]  the maker's plain name (A6)
 */
export function renderTrumpBadge(ctx, layout, state, opts = {}) {
  const { badgeW, badgeH } = layout;
  const labelGap = LABEL_GAP;
  const labelH = LABEL_H;

  // B1-5 / Y3: one shared box (pinned on phones, beside the maker's
  // plate on desktop).
  const box = trumpBadgeRect(layout, state);
  if (!box) return;
  const bx = box.x;
  const by = box.y;

  // M5 #13, phosphor glow when trump_suit is set. ctx.shadow* applies
  // to subsequent fill ops, so we set it before the badge fill. Pre-bid
  // (no suit) renders muted and ungloved so the placeholder doesn't
  // pretend to be live trump.
  ctx.save();
  if (state.trump_suit) {
    ctx.shadowColor = suitColor(state.trump_suit);
    ctx.shadowBlur  = 14;
  }
  // Background fill: suit-natural color when set, muted gray otherwise.
  ctx.fillStyle = suitColor(state.trump_suit);
  fillRoundRect(ctx, bx, by, badgeW, badgeH, 8);
  ctx.restore();

  // Symbol or em-dash. BOTH symbol + name drawn (OQ-7).
  ctx.fillStyle = COLOR_BADGE_TEXT;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = canvasFont(28, { weight: 600 });
  const symbol = state.trump_suit ? SUIT_SYMBOL[state.trump_suit] : '—';
  ctx.fillText(symbol, bx + Math.round(badgeW / 2), by + Math.round(badgeH / 2));

  // Suit name (or "No trump") below the badge.
  // #design-a11y: "No trump" / "Diamonds" are wider than the 48px badge and
  // were clipping ("No trump" → "Bo trump"). Shrink the label to fit a box
  // a little wider than the badge (the centered text may overhang the badge
  // edges slightly, but the anchor clamp above keeps it on-canvas).
  // A6: on phones the label names the caller; its box is the stack's label
  // row cut to the canvas (the portrait badge sits 6px from the left edge),
  // and a long name gives way with "…".
  ctx.fillStyle = state.trump_suit ? COLOR_BADGE_TEXT : COLOR_LABEL_MUTED;
  const names = opts.makerName && state.maker_seat ? { [state.maker_seat]: opts.makerName } : undefined;
  const full = trumpBadgeLabel(layout, state, names);
  const boxLeft = Math.max(0, bx - LABEL_EXTRA_W / 2);
  const boxRight = Math.min(layout.canvasW, bx + badgeW + LABEL_EXTRA_W / 2);
  const labelBox = boxRight - boxLeft;
  const labelPx = fitFontSize(full, labelBox, (px) => {
    ctx.font = canvasFont(px);
    return ctx.measureText(full).width;
  }, { basePx: 11, minPx: 8 });
  ctx.font = canvasFont(labelPx);
  const name = truncateLabelToFit(full, labelBox, (t) => ctx.measureText(t).width);
  const textW = ctx.measureText(name).width;
  // Centred on the badge unless the canvas edge cuts the box.
  const textCx = Math.min(Math.max(bx + Math.round(badgeW / 2), boxLeft + textW / 2), boxRight - textW / 2);
  const textCy = by + badgeH + labelGap + Math.round(labelH / 2);
  ctx.fillText(name, textCx, textCy);
  // B4b-1 e2e seam: "text|x,y,w,h" of the drawn label.
  const el = ctx.canvas;
  if (el && el.dataset) {
    el.dataset.trumpLabel = `${name}|${Math.floor(textCx - textW / 2)},${by + badgeH + labelGap},${Math.ceil(textW)},${labelH}`;
  }
}

/**
 * Render a mini trump pip at (cx, cy) with diameter `size`. Used by the
 * scoreboard as a glance-down mirror. Renders nothing when suit is null
 * so the score panel collapses cleanly pre-bid.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {string|null|undefined} suit
 * @param {number} cx  center X
 * @param {number} cy  center Y
 * @param {number} size  diameter in px (≤24 per plan AC #5)
 */
export function renderTrumpPip(ctx, suit, cx, cy, size) {
  if (!suit) return;
  // M5 #13, phosphor glow on the mini pip so the scoreboard mirror
  // shares the same Pillar 2 channel as the main badge.
  ctx.save();
  ctx.shadowColor = suitColor(suit);
  ctx.shadowBlur  = 6;
  // Filled circle in the suit's natural color, then the suit glyph on top.
  const r = Math.floor(size / 2);
  ctx.fillStyle = suitColor(suit);
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  ctx.fillStyle = COLOR_BADGE_TEXT;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // Glyph at ~70% of pip diameter; rounded so the system-ui suit
  // character renders crisply at small sizes.
  const fontPx = Math.max(10, Math.round(size * 0.72));
  ctx.font = canvasFont(fontPx, { weight: 600 });
  ctx.fillText(SUIT_SYMBOL[suit], cx, cy + 1);
}

export const _internals = {
  COLOR_SUIT_RED,
  COLOR_SUIT_DARK,
  COLOR_BADGE_NONE,
  COLOR_BADGE_TEXT,
  COLOR_LABEL_MUTED,
  SUIT_SYMBOL,
  SUIT_NAME,
  suitColor,
};
